import React, { Fragment, useState, useEffect } from "react";
import { Message, Button } from "semantic-ui-react";
import moment from "moment-timezone";

import useApiService from "../../services/useApiService";
import {
  getText,
  getTimeDuration,
  getValidAccountableManagers,
  isValidPersonFormat,
  extractPersonDetails,
  flattenAccountableManagers,
} from "../../helpers";
import SemanticUiModal from "../semantic_ui_modal";
import InspectionOwnerDropdown from "./inspection_owner_dropdown";
import ActivityDescription from "./activity_description";
import InspectionsTable from "./inspection_table";

export default function CreateInspectionModal(props) {
  const { planId, workPhase, activity, inspections = [], hideCreateInspectionModal, confirmCreateInspection } = props;
  const [lastInspection = { created_at: null, completed_date: null }] = inspections;

  const lastInspectionCreatedAt = lastInspection.created_at && moment(lastInspection.created_at);
  const timeDuration = getTimeDuration(lastInspectionCreatedAt);

  const lastInspectionCompletedAt = lastInspection.completed_date && moment(lastInspection.completed_date);
  const compltedTimeDuration = getTimeDuration(lastInspectionCompletedAt);

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezoneAbbr = moment().tz(timezone).zoneAbbr();
  const timeZoneOffset = moment().format("Z");
  const timezoneDesc = `${timezone} (${timezoneAbbr} UTC${timeZoneOffset})`;

  const [inspectionOwners, setInspectionOwners] = useState([]);
  const [peopleOptions, setPeopleOptions] = useState([]);
  const [multipleInspctnOwnrsErr, setMultipleInspctnOwnrsErr] = useState(false);
  const [invalidPersonFormatErr, setInvalidPersonFormatErr] = useState(false);
  const currentInspectionOwner = inspectionOwners[0];

  const riskDescription = `${workPhase} / ${activity}`;
  const activityAcctbleMngrsPayload = { params: { plan_id: planId, risk_description: riskDescription, risk_level: "activity", distinct: true } };
  const [{ response: unverifiedActvtyAcctbleMngrs }] = useApiService({
    url: `/risks/accountable_managers`,
    payload: activityAcctbleMngrsPayload,
    onMount: true,
    onSuccess: setDefaultInspectionOwners,
    defaultResponse: [],
  });
  const [{ response: accountableManagers, isLoading: isLoadingAccountingManagers }] = useApiService({
    url: `/risks/accountable_managers`,
    payload: { params: { distinct: true } },
    onMount: true,
    defaultResponse: [],
  });
  const [{ response: users, isLoading: isLoadingUsers }] = useApiService({
    url: `/users`,
    onMount: true,
    defaultResponse: [],
  });

  function setDefaultInspectionOwners(unverifiedActvtyAcctbleMngrs) {
    const managers = flattenAccountableManagers(unverifiedActvtyAcctbleMngrs);
    const validInspectionOwners = getValidAccountableManagers(managers);
    setInspectionOwners(validInspectionOwners);
    if (validInspectionOwners.length > 1) return setMultipleInspctnOwnrsErr(true);
  }

  useEffect(() => {
    const userDetails = users.map((user) => {
      const { first_name, last_name } = user.profile || {};
      return `${first_name} ${last_name} (${user.email})`;
    });
    const userOptions = getValidAccountableManagers(userDetails);
    const accountingManagerOptions = getValidAccountableManagers(accountableManagers);
    const uniqueOptions = [...new Set(userOptions.concat(accountingManagerOptions))];
    setPeopleOptions(uniqueOptions);
  }, [users, accountableManagers]);

  const validateInspectionOwners = (inspectionOwners) => {
    setMultipleInspctnOwnrsErr(false);
    setInvalidPersonFormatErr(false);

    if (inspectionOwners[0] && !isValidPersonFormat(inspectionOwners[0])) return setInvalidPersonFormatErr(true);
    if (inspectionOwners.length > 1) return setMultipleInspctnOwnrsErr(true);
  };

  const onInspectionOwnersChange = (newInspectionOwners) => {
    setInspectionOwners(newInspectionOwners);
    validateInspectionOwners(newInspectionOwners);
  };

  const removeInspectionOwner = (evt, owner) => {
    const newInspectionOwners = inspectionOwners.filter((inspectionOwner) => inspectionOwner !== owner);
    setInspectionOwners(newInspectionOwners);
    validateInspectionOwners(newInspectionOwners);
  };

  const onSaveCreateNew = (newInspectionOwner) => {
    setInspectionOwners([newInspectionOwner]);

    if (peopleOptions.includes(newInspectionOwner)) return;
    peopleOptions.unshift(newInspectionOwner);
    setPeopleOptions(peopleOptions);
    validateInspectionOwners(peopleOptions);
  };

  const onCreateNewInspection = (event) => {
    const { firstName: first_name, lastName: last_name, email } = extractPersonDetails(currentInspectionOwner);
    const newInspectionDetails = { inspection_owner: { first_name, last_name, email } };
    confirmCreateInspection(event, newInspectionDetails);
  };

  const isLoadingInspectionOwners = isLoadingUsers || isLoadingAccountingManagers;
  const disableCreateInspection = !currentInspectionOwner || multipleInspctnOwnrsErr || invalidPersonFormatErr;

  return (
    <SemanticUiModal
      modalOptions={{
        className: "create-inspection-modal",
      }}
      header={
        <Fragment>
          <div>{getText("Create Inspection")}</div>
          <ActivityDescription workPhase={workPhase} activity={activity} />
        </Fragment>
      }
      scrollContent
      content={
        <Fragment>
          {inspections.length > 0 && (
            <div className="last-inspection-details">
              {lastInspectionCompletedAt ? (
                <Message color="orange" className="">
                  {getText("A completed inspection already exists for this activity.")} {getText("The inspection was completed on")} '
                  {lastInspectionCompletedAt.format("DD MMM YYYY hh:mm A")} {compltedTimeDuration && `(${compltedTimeDuration})`}'.
                </Message>
              ) : (
                <Message color="orange" className="">
                  {getText("An incomplete inspection already exists for this activity.")} {getText("The inspection was created on")} '
                  {lastInspectionCreatedAt.format("DD MMM YYYY hh:mm A")} {timeDuration && `(${timeDuration})`}'.
                </Message>
              )}
              <div className="">
                <label>{getText("Inspection Details")}</label>
              </div>
              <div className="timezone-description">
                {getText("Timezone")}: {timezoneDesc}
              </div>
              <InspectionsTable inspections={inspections.slice(0, 3)} />
              <div style={{ marginTop: "0.5rem" }}>
                {getText("The above list is limited to the last 3 inspections. To see all of the inspections, go to ")}
                <a href="https://safetyculture.com/" target="_blank">{getText("SafetyCulture")}</a>
              </div>
            </div>
          )}
          <div>
            <label>{getText("Create new inspection for this activity")}</label>
            <div>{getText("Assign the inspection owner, then click 'Create New Inspection' button to create a new inspection.")}</div>
            <div className="inspection-owner-container">
              <InspectionOwnerDropdown
                className="inspection-owner-dropdown"
                placeholder="Inspection Owner"
                addPersonLabel="Add Inspection Owner"
                inspectionOwners={inspectionOwners}
                peopleOptions={peopleOptions}
                onInspectionOwnersChange={onInspectionOwnersChange}
                removeInspectionOwner={removeInspectionOwner}
                onSaveCreateNew={onSaveCreateNew}
                isLoading={isLoadingInspectionOwners}
              />
            </div>
            {multipleInspctnOwnrsErr && (
              <Message color="red" className="">
                <div>
                  {getText(
                    "The system has detected that this activity has been assigned to multiple people. Please choose who will be the inspection owner."
                  )}
                </div>
                <ul>
                  {flattenAccountableManagers(unverifiedActvtyAcctbleMngrs).map((owner, index) => {
                    return <li key={index}>{owner}</li>;
                  })}
                </ul>
              </Message>
            )}
            {invalidPersonFormatErr && (
              <Message color="red" className="">
                <div>
                  {getText(
                    "The system has detected that this activity has been assigned with invalid person format. Required Format - eg. John Doe (john.doe@teck.com). Please choose who will be the inspection owner."
                  )}
                </div>
                <ul>
                  {flattenAccountableManagers(unverifiedActvtyAcctbleMngrs).map((owner, index) => {
                    return <li key={index}>{owner}</li>;
                  })}
                </ul>
              </Message>
            )}
          </div>
        </Fragment>
      }
      actions={
        <div>
          <Button positive size="medium" className="" onClick={onCreateNewInspection} disabled={disableCreateInspection}>
            {getText("Create New Inspection")}
          </Button>
        </div>
      }
      closeModal={hideCreateInspectionModal}
    />
  );
}



// WEBPACK FOOTER //
// ./src/components/create_inspection_link/create_inspection_modal.js